import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    CardHeader,
    Grid,
    Chip,
    Stack,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Rating,
    TextField,
    Paper,
    Divider,
    IconButton,
    Avatar,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    StepConnector,
    Breadcrumbs,
    Link,
    Tooltip,
    Fab,
    Badge
} from '@mui/material';
import {
    ArrowBack,
    Build,
    Warning,
    CheckCircle,
    PlayArrow,
    ThumbUp,
    ThumbDown,
    Comment,
    Visibility,
    ExpandMore,
    Error,
    Speed,
    ElectricalServices,
    LocalGasStation,
    Thermostat,
    Settings,
    Psychology,
    Star,
    Schedule,
    Person,
    Home,
    VideoPlay,
    Bookmark,
    BookmarkBorder,
    Share,
    Print,
    NavigateNext,
    Check,
    AccessTime,
    BuildCircle,
    NavigateBefore,
    Feedback
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../api';

const CATEGORY_CONFIG = {
    engine_issues: { icon: Build, label: 'Engine Issues', color: '#f44336' },
    electrical_problems: { icon: ElectricalServices, label: 'Electrical Problems', color: '#ff9800' },
    cooling_system: { icon: Thermostat, label: 'Cooling System', color: '#2196f3' },
    fuel_system: { icon: LocalGasStation, label: 'Fuel System', color: '#4caf50' },
    generator_issues: { icon: Speed, label: 'Generator Issues', color: '#9c27b0' },
    maintenance_procedures: { icon: Settings, label: 'Maintenance', color: '#607d8b' },
    diagnostic_codes: { icon: Psychology, label: 'Diagnostic Codes', color: '#795548' },
    preventive_care: { icon: CheckCircle, label: 'Preventive Care', color: '#8bc34a' }
};

const DIFFICULTY_COLORS = {
    beginner: '#4caf50',
    intermediate: '#ff9800',
    advanced: '#f44336',
    expert: '#9c27b0'
};

const PRIORITY_COLORS = {
    low: '#4caf50',
    medium: '#ff9800',
    high: '#f44336',
    critical: '#d32f2f'
};

function APUGuideDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    
    const [guide, setGuide] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeStep, setActiveStep] = useState(0);
    const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
    const [rating, setRating] = useState(0);
    const [comment, setComment] = useState('');
    const [wasHelpful, setWasHelpful] = useState(null);
    const [additionalTips, setAdditionalTips] = useState('');
    const [submittingFeedback, setSubmittingFeedback] = useState(false);
    const [isBookmarked, setIsBookmarked] = useState(false);

    useEffect(() => {
        fetchGuide();
    }, [id]);

    const fetchGuide = async () => {
        try {
            setLoading(true);
            const response = await apiClient.get(`/apu-troubleshooting/${id}`);
            setGuide(response.data.data);
            setError(null);
            
            // Check if user already provided feedback
            const userFeedback = response.data.data.feedback?.find(
                fb => fb.userId._id === user?.id
            );
            if (userFeedback) {
                setRating(userFeedback.rating);
                setComment(userFeedback.comment || '');
                setWasHelpful(userFeedback.wasHelpful);
                setAdditionalTips(userFeedback.additionalTips || '');
            }
        } catch (err) {
            console.error('Error fetching guide:', err);
            setError('Failed to load troubleshooting guide');
        } finally {
            setLoading(false);
        }
    };

    const handleStepClick = (stepIndex) => {
        setActiveStep(stepIndex);
    };

    const handleNextStep = () => {
        if (activeStep < guide.troubleshootingSteps.length - 1) {
            setActiveStep(activeStep + 1);
        }
    };

    const handlePrevStep = () => {
        if (activeStep > 0) {
            setActiveStep(activeStep - 1);
        }
    };

    const handleSubmitFeedback = async () => {
        if (!rating || wasHelpful === null) {
            alert('Please provide a rating and indicate if this guide was helpful.');
            return;
        }

        try {
            setSubmittingFeedback(true);
            await apiClient.post(`/apu-troubleshooting/${id}/feedback`, {
                rating,
                comment: comment.trim(),
                wasHelpful,
                additionalTips: additionalTips.trim()
            });
            
            setFeedbackDialogOpen(false);
            await fetchGuide(); // Refresh to show new feedback
        } catch (err) {
            console.error('Error submitting feedback:', err);
            alert('Failed to submit feedback. Please try again.');
        } finally {
            setSubmittingFeedback(false);
        }
    };

    const getSeverityIcon = (priority) => {
        switch (priority) {
            case 'critical': return <Error sx={{ color: PRIORITY_COLORS[priority] }} />;
            case 'high': return <Warning sx={{ color: PRIORITY_COLORS[priority] }} />;
            case 'medium': return <Speed sx={{ color: PRIORITY_COLORS[priority] }} />;
            default: return <CheckCircle sx={{ color: PRIORITY_COLORS[priority] }} />;
        }
    };

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ py: 3 }}>
                <Box sx={{ textAlign: 'center', py: 8 }}>
                    <CircularProgress size={48} />
                    <Typography variant="h6" sx={{ mt: 2 }}>
                        Loading troubleshooting guide...
                    </Typography>
                </Box>
            </Container>
        );
    }

    if (error || !guide) {
        return (
            <Container maxWidth="lg" sx={{ py: 3 }}>
                <Alert severity="error">
                    {error || 'Guide not found'}
                </Alert>
                <Button 
                    startIcon={<ArrowBack />} 
                    onClick={() => navigate('/safety/apu-troubleshooting')}
                    sx={{ mt: 2 }}
                >
                    Back to Troubleshooting Center
                </Button>
            </Container>
        );
    }

    const CategoryIcon = CATEGORY_CONFIG[guide.category]?.icon || Build;
    const userFeedback = guide.feedback?.find(fb => fb.userId._id === user?.id);

    return (
        <Container maxWidth="lg" sx={{ py: 3 }}>
            {/* Breadcrumbs */}
            <Breadcrumbs sx={{ mb: 3 }}>
                <Link color="inherit" onClick={() => navigate('/safety')} sx={{ cursor: 'pointer' }}>
                    Safety Center
                </Link>
                <Link color="inherit" onClick={() => navigate('/safety/apu-troubleshooting')} sx={{ cursor: 'pointer' }}>
                    APU Troubleshooting
                </Link>
                <Typography color="text.primary">Guide Detail</Typography>
            </Breadcrumbs>

            {/* Header */}
            <Card sx={{ mb: 3 }}>
                <CardHeader
                    avatar={
                        <Avatar sx={{ bgcolor: CATEGORY_CONFIG[guide.category]?.color }}>
                            <CategoryIcon />
                        </Avatar>
                    }
                    action={
                        <Stack direction="row" spacing={1}>
                            <IconButton 
                                onClick={() => setIsBookmarked(!isBookmarked)}
                                color={isBookmarked ? 'primary' : 'default'}
                            >
                                {isBookmarked ? <Bookmark /> : <BookmarkBorder />}
                            </IconButton>
                            <IconButton>
                                <Share />
                            </IconButton>
                            <IconButton>
                                <Print />
                            </IconButton>
                        </Stack>
                    }
                    title={
                        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                            {guide.title}
                        </Typography>
                    }
                    subheader={
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }}>
                            <Chip 
                                icon={<CategoryIcon />}
                                label={CATEGORY_CONFIG[guide.category]?.label} 
                                color="primary"
                                variant="outlined"
                            />
                            <Chip 
                                label={guide.difficulty} 
                                sx={{ 
                                    bgcolor: DIFFICULTY_COLORS[guide.difficulty],
                                    color: 'white'
                                }}
                            />
                            <Chip 
                                icon={getSeverityIcon(guide.priority)}
                                label={`${guide.priority} priority`} 
                                sx={{ 
                                    bgcolor: PRIORITY_COLORS[guide.priority],
                                    color: 'white'
                                }}
                            />
                        </Stack>
                    }
                />
                <CardContent>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                        {guide.description}
                    </Typography>

                    {/* Metadata */}
                    <Grid container spacing={2}>
                        <Grid item xs={6} sm={3}>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Visibility sx={{ fontSize: 18, color: 'text.secondary' }} />
                                <Typography variant="body2">
                                    {guide.metadata?.viewCount || 0} views
                                </Typography>
                            </Stack>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Star sx={{ fontSize: 18, color: '#ffc107' }} />
                                <Typography variant="body2">
                                    {guide.metadata?.averageRating?.toFixed(1) || 'N/A'} rating
                                </Typography>
                            </Stack>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} />
                                <Typography variant="body2">
                                    {guide.metadata?.successfulResolutions || 0} solved
                                </Typography>
                            </Stack>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Person sx={{ fontSize: 18, color: 'text.secondary' }} />
                                <Typography variant="body2">
                                    {guide.createdBy?.name || 'System'}
                                </Typography>
                            </Stack>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            <Grid container spacing={3}>
                {/* Main Content */}
                <Grid item xs={12} lg={8}>
                    {/* Symptoms */}
                    {guide.symptoms && guide.symptoms.length > 0 && (
                        <Card sx={{ mb: 3 }}>
                            <CardHeader title="⚠️ Symptoms & Warning Signs" />
                            <CardContent>
                                <List>
                                    {guide.symptoms.map((symptom, index) => (
                                        <ListItem key={index}>
                                            <ListItemIcon>
                                                {getSeverityIcon(symptom.severity)}
                                            </ListItemIcon>
                                            <ListItemText primary={symptom.description} />
                                        </ListItem>
                                    ))}
                                </List>
                            </CardContent>
                        </Card>
                    )}

                    {/* Video Walkthrough */}
                    {guide.videoWalkthrough?.url && (
                        <Card sx={{ mb: 3 }}>
                            <CardHeader 
                                title="📹 Video Walkthrough"
                                subheader={guide.videoWalkthrough.duration ? `Duration: ${guide.videoWalkthrough.duration}` : ''}
                            />
                            <CardContent>
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    Watch this video for a complete walkthrough before starting the troubleshooting steps.
                                </Alert>
                                <Button
                                    variant="contained"
                                    startIcon={<PlayArrow />}
                                    fullWidth
                                    size="large"
                                    onClick={() => window.open(guide.videoWalkthrough.url, '_blank')}
                                >
                                    {guide.videoWalkthrough.title || 'Play Video Walkthrough'}
                                </Button>
                            </CardContent>
                        </Card>
                    )}

                    {/* Troubleshooting Steps */}
                    <Card sx={{ mb: 3 }}>
                        <CardHeader 
                            title="🔧 Step-by-Step Instructions"
                            subheader={`${guide.troubleshootingSteps?.length || 0} steps total`}
                        />
                        <CardContent>
                            <Stepper 
                                activeStep={activeStep} 
                                orientation="vertical"
                                sx={{ mb: 3 }}
                            >
                                {guide.troubleshootingSteps?.map((step, index) => (
                                    <Step key={index}>
                                        <StepLabel onClick={() => handleStepClick(index)} sx={{ cursor: 'pointer' }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                {step.title}
                                            </Typography>
                                        </StepLabel>
                                        <StepContent>
                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="body1" sx={{ mb: 2 }}>
                                                    {step.description}
                                                </Typography>

                                                {step.safetyWarning && (
                                                    <Alert severity="warning" sx={{ mb: 2 }}>
                                                        <strong>Safety Warning:</strong> {step.safetyWarning}
                                                    </Alert>
                                                )}

                                                {step.toolsRequired && step.toolsRequired.length > 0 && (
                                                    <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                                            🔨 Tools Required:
                                                        </Typography>
                                                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                                            {step.toolsRequired.map((tool, toolIndex) => (
                                                                <Chip 
                                                                    key={toolIndex} 
                                                                    label={tool} 
                                                                    size="small" 
                                                                    variant="outlined"
                                                                />
                                                            ))}
                                                        </Stack>
                                                    </Paper>
                                                )}

                                                {step.estimatedTime && (
                                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                                        <AccessTime sx={{ fontSize: 18, color: 'text.secondary' }} />
                                                        <Typography variant="body2" color="text.secondary">
                                                            Estimated time: {step.estimatedTime}
                                                        </Typography>
                                                    </Stack>
                                                )}

                                                {step.expectedResult && (
                                                    <Paper sx={{ p: 2, mb: 2, bgcolor: 'success.50', borderLeft: 3, borderColor: 'success.main' }}>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                                            ✅ Expected Result:
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {step.expectedResult}
                                                        </Typography>
                                                    </Paper>
                                                )}
                                            </Box>

                                            <Stack direction="row" spacing={1}>
                                                <Button
                                                    size="small"
                                                    onClick={handlePrevStep}
                                                    disabled={activeStep === 0}
                                                    startIcon={<NavigateBefore />}
                                                >
                                                    Previous
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    onClick={handleNextStep}
                                                    disabled={activeStep === guide.troubleshootingSteps.length - 1}
                                                    endIcon={<NavigateNext />}
                                                >
                                                    {activeStep === guide.troubleshootingSteps.length - 1 ? 'Complete' : 'Next Step'}
                                                </Button>
                                            </Stack>
                                        </StepContent>
                                    </Step>
                                ))}
                            </Stepper>

                            {activeStep === guide.troubleshootingSteps?.length - 1 && (
                                <Alert severity="success" sx={{ mt: 2 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                        🎉 Troubleshooting Complete!
                                    </Typography>
                                    <Typography variant="body2">
                                        If these steps resolved your issue, please consider leaving feedback to help other drivers.
                                    </Typography>
                                </Alert>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Sidebar */}
                <Grid item xs={12} lg={4}>
                    {/* Quick Actions */}
                    <Card sx={{ mb: 3 }}>
                        <CardHeader title="Quick Actions" />
                        <CardContent>
                            <Stack spacing={2}>
                                <Button
                                    variant="contained"
                                    fullWidth
                                    startIcon={<Feedback />}
                                    onClick={() => setFeedbackDialogOpen(true)}
                                    disabled={!!userFeedback}
                                >
                                    {userFeedback ? 'Feedback Submitted' : 'Rate This Guide'}
                                </Button>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    startIcon={<ArrowBack />}
                                    onClick={() => navigate('/safety/apu-troubleshooting')}
                                >
                                    Back to Guides
                                </Button>
                            </Stack>
                        </CardContent>
                    </Card>

                    {/* Recent Feedback */}
                    {guide.feedback && guide.feedback.length > 0 && (
                        <Card sx={{ mb: 3 }}>
                            <CardHeader title="Driver Feedback" />
                            <CardContent>
                                <Stack spacing={2}>
                                    {guide.feedback.slice(-3).map((feedback, index) => (
                                        <Paper key={index} sx={{ p: 2, bgcolor: 'grey.50' }}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                                    {feedback.userId?.name || 'Anonymous'}
                                                </Typography>
                                                <Rating value={feedback.rating} readOnly size="small" />
                                            </Stack>
                                            {feedback.comment && (
                                                <Typography variant="body2" sx={{ mb: 1 }}>
                                                    {feedback.comment}
                                                </Typography>
                                            )}
                                            {feedback.additionalTips && (
                                                <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'primary.main' }}>
                                                    💡 Tip: {feedback.additionalTips}
                                                </Typography>
                                            )}
                                        </Paper>
                                    ))}
                                </Stack>
                            </CardContent>
                        </Card>
                    )}

                    {/* Version Info */}
                    <Card>
                        <CardHeader title="Guide Information" />
                        <CardContent>
                            <Stack spacing={1}>
                                <Typography variant="body2">
                                    <strong>Version:</strong> {guide.versioning?.version || '1.0.0'}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Last Updated:</strong> {
                                        guide.versioning?.lastUpdated ? 
                                        new Date(guide.versioning.lastUpdated).toLocaleDateString() :
                                        'Unknown'
                                    }
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Created by:</strong> {guide.createdBy?.name || 'System'}
                                </Typography>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Feedback Dialog */}
            <Dialog open={feedbackDialogOpen} onClose={() => setFeedbackDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Rate This Troubleshooting Guide</DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        <Box>
                            <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                How would you rate this guide?
                            </Typography>
                            <Rating
                                value={rating}
                                onChange={(event, newValue) => setRating(newValue)}
                                size="large"
                            />
                        </Box>

                        <Box>
                            <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                Was this guide helpful in solving your problem?
                            </Typography>
                            <Stack direction="row" spacing={1}>
                                <Button
                                    variant={wasHelpful === true ? 'contained' : 'outlined'}
                                    startIcon={<ThumbUp />}
                                    onClick={() => setWasHelpful(true)}
                                    color="success"
                                >
                                    Yes
                                </Button>
                                <Button
                                    variant={wasHelpful === false ? 'contained' : 'outlined'}
                                    startIcon={<ThumbDown />}
                                    onClick={() => setWasHelpful(false)}
                                    color="error"
                                >
                                    No
                                </Button>
                            </Stack>
                        </Box>

                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label="Comments (optional)"
                            placeholder="Share your experience with this guide..."
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                        />

                        <TextField
                            fullWidth
                            multiline
                            rows={2}
                            label="Additional Tips (optional)"
                            placeholder="Any extra tips or suggestions for other drivers..."
                            value={additionalTips}
                            onChange={(e) => setAdditionalTips(e.target.value)}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFeedbackDialogOpen(false)}>Cancel</Button>
                    <Button
                        onClick={handleSubmitFeedback}
                        disabled={!rating || wasHelpful === null || submittingFeedback}
                        variant="contained"
                    >
                        {submittingFeedback ? 'Submitting...' : 'Submit Feedback'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}

export default APUGuideDetail; 